import { faAngleLeft } from '@fortawesome/pro-regular-svg-icons/faAngleLeft';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons/faAngleRight';
import { faCamera } from '@fortawesome/pro-regular-svg-icons/faCamera';
import { faMinus } from '@fortawesome/pro-regular-svg-icons/faMinus';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import useEventListener from '@propertypal/shared/src/hooks/useEventListener';
import detachedHouse from '@propertypal/shared/src/resources/icons/filters/detached-icon.svg?url';
import panoramicIcon from '@propertypal/shared/src/resources/icons/property/panoramic-icon.svg?url';
import { GalleryItem } from '@propertypal/shared/src/utils/property/getGalleryImages';
import useTouchDevice from '@propertypal/web-app/src/hooks/useTouchDevice';
import React, {
  FunctionComponent,
  KeyboardEventHandler,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTheme } from 'styled-components';
import 'swiper/css';
import { Lazy } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperClass, SwiperOptions } from 'swiper/types';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import Magnifier from '../magnifier/Magnifier';
import { Text } from '../typography';
import PanZoomImage, { DEFAULT_ZOOM_POSITION, UsePanZoomImageData, usePanZoomImage } from '../zoom/PanZoomImage';
import {
  ArrowButton,
  Container,
  CounterContainer,
  Curtain,
  ImageBox,
  ImageOverlay,
  NoImageBackground,
  PanoramicIconBox,
  PhotoCounter,
  ZoomContainer,
  ZoomControl,
  ZoomControls,
} from './PropertySlider.style';
import TourIcon from './TourIcon';

export interface PropertySliderProps {
  alt: string;
  images: GalleryItem[];
  onClick?: (item: GalleryItem) => void;
  onCounterClick?: (item: GalleryItem) => void;
  imageIndex: number;
  setImageIndex: (index: number) => void;
  centerImageCounter?: boolean;
  renderImageOverlay?: () => ReactElement;
  fullscreen?: boolean;
  enableKeyboardControls?: boolean;
  updateOnIndexChange?: boolean;
  children?: ReactNode;
  sitemap?: boolean;
  floorPlans?: boolean;
  altCounter?: boolean;
  slidesPerView?: SwiperOptions['slidesPerView'];
  maxHeight: string;
  hideCounter?: boolean;
  scrollTop?: number;
}

const MAX_RATIO = 1.65;
const MIN_RATIO = 1;

const calculateRatio = (images: GalleryItem[]) => {
  let ratio: number = 0;

  images.forEach((image) => {
    const iRatio = image.width / image.height;

    if (ratio === 0 || iRatio > ratio) {
      ratio = Math.min(MAX_RATIO, iRatio);
    }
  });

  return Math.max(MIN_RATIO, ratio);
};

const getInitialIndexes = (images: GalleryItem[], initialIndex: number) => {
  if (initialIndex === 0) {
    return [images.length - 1, 0, 1];
  }

  if (initialIndex === images.length - 1) {
    return [initialIndex - 1, initialIndex, 0];
  }

  return [initialIndex - 1, initialIndex, initialIndex + 1];
};

interface PropertySliderCounterProps
  extends Pick<PropertySliderProps, 'centerImageCounter' | 'onCounterClick' | 'altCounter' | 'imageIndex' | 'images'> {}

export const PropertySliderCounter: React.FC<PropertySliderCounterProps> = (props) => {
  const theme = useTheme();

  const handleCounterClick = () => {
    if (props.onCounterClick && props.images[props.imageIndex]) {
      props.onCounterClick(props.images[props.imageIndex]);
    }
  };

  return (
    <CounterContainer center={props.centerImageCounter} data-testid="property-slider-counter">
      <PhotoCounter
        aria-label="Open photos dialog"
        onClick={handleCounterClick}
        as={props.onCounterClick ? 'button' : 'div'}
        $alt={props.altCounter}
        data-testid="photoCounter"
      >
        {!props.altCounter && (
          <FontAwesomeIcon
            icon={faCamera}
            color={theme.white}
            style={{ fontSize: props.altCounter ? 15 : 12, marginTop: 2, marginRight: 4 }}
          />
        )}
        <Text
          fontSize={props.altCounter ? 15 : 12}
          color={theme.white}
          fontWeight={props.altCounter ? 'bold' : undefined}
        >
          {props.imageIndex + 1}
          {props.altCounter ? ' of ' : '/'}
          {props.images.length}
        </Text>
      </PhotoCounter>
    </CounterContainer>
  );
};

const FloorPlanZoomControls: FunctionComponent<UsePanZoomImageData> = (props) => {
  return (
    <ZoomContainer>
      <ZoomControls>
        <ZoomControl
          icon={faMinus}
          onClick={() => {
            props.zoomOut();
          }}
          aria-disabled={props.position.z === 1}
          $isDisabled={props.position.z === 1}
          data-testid="zoom-out"
        />
        <p data-testid="zoom-amount">{props.zoomPercentage}%</p>
        <ZoomControl
          icon={faPlus}
          onClick={() => {
            props.zoomIn();
          }}
          aria-disabled={props.position.z === 2}
          $isDisabled={props.position.z === 2}
          data-testid="zoom-in"
        />
      </ZoomControls>
    </ZoomContainer>
  );
};

const PropertySlider: FunctionComponent<PropertySliderProps> = (props) => {
  const theme = useTheme();
  const touchDevice = useTouchDevice();
  const lastKeyPress = useRef<number>(0);
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);
  const aspectRatio = calculateRatio(props.images);
  const zoomImage = usePanZoomImage();
  const initialIndexes = useRef(getInitialIndexes(props.images, props.imageIndex));

  // get active index when using floor plans for zoom functionality
  const activeIndex = props.imageIndex;

  const incrementIndex = () => {
    swiper?.slideNext();
  };

  const decrementIndex = () => {
    swiper?.slidePrev();
  };

  const handleImageClick = (image: GalleryItem) => {
    if (swiper) {
      const imageIndex = swiper.realIndex;

      if (image.index === imageIndex) {
        if (props.onClick) props.onClick(image);
      } else if (
        (image.index < imageIndex || (imageIndex === 0 && image.index === props.images.length - 1)) &&
        !(imageIndex === props.images.length - 1 && image.index === 0)
      ) {
        decrementIndex();
      } else {
        incrementIndex();
      }
    }
  };

  const handleKeyPress: KeyboardEventHandler = (event) => {
    const now = new Date().getTime();

    if (
      document.scrollingElement &&
      document.scrollingElement.scrollTop <= ((!props.fullscreen && props.scrollTop) || 0) &&
      now > lastKeyPress.current + 500
    ) {
      if (event.key === 'ArrowRight') {
        incrementIndex();
      } else if (event.key === 'ArrowLeft') {
        decrementIndex();
      }

      lastKeyPress.current = now;
    }
  };

  const handleKeyUp = () => {
    lastKeyPress.current = 0;
  };

  useEventListener('keydown', handleKeyPress, props.enableKeyboardControls);
  useEventListener('keyup', handleKeyUp, props.enableKeyboardControls);

  useEffect(() => {
    if (props.updateOnIndexChange && swiper && swiper.realIndex !== props.imageIndex) {
      swiper.slideToLoop(props.imageIndex, 0);
    }
  }, [props.updateOnIndexChange]);

  useEffect(() => {
    // reset zoom position on index change
    if (activeIndex != null && props.floorPlans) zoomImage.setPosition(DEFAULT_ZOOM_POSITION);
  }, [activeIndex, props.floorPlans]);

  if (props.images.length === 0) {
    return (
      <Container role="region" aria-label="property images" tabIndex={0}>
        <NoImageBackground data-testid="no-image">
          {props.children}
          {props.renderImageOverlay && props.renderImageOverlay()}

          <img src={detachedHouse.src} width={detachedHouse.width} height={detachedHouse.height} alt="house" />
          <Text>No photo available</Text>
        </NoImageBackground>
      </Container>
    );
  }

  return (
    <Container style={{ aspectRatio, height: '100%', maxHeight: props.maxHeight }}>
      {props.images && !props.hideCounter && (
        <PropertySliderCounter
          imageIndex={props.imageIndex}
          images={props.images}
          altCounter={props.altCounter}
          centerImageCounter={props.centerImageCounter}
          onCounterClick={props.onCounterClick}
        />
      )}

      {props.images && props.floorPlans && <FloorPlanZoomControls {...zoomImage} />}

      {/* Blocks ability on iOS to swipe history back and swipe the slider at the same time */}
      {/* Doing both at the same time creates a race condition between history back and history replace */}
      <Curtain style={{ left: 0 }} />
      <Curtain style={{ right: 0 }} />

      <Swiper
        initialSlide={props.imageIndex}
        onSlideChange={(mSwiper) => {
          const index = mSwiper.realIndex;

          // Timeout prevents state updates happening during slide transition which causes animation lag
          if (props.imageIndex !== index) {
            setTimeout(() => {
              props.setImageIndex(index);
            }, 300);
          }
        }}
        modules={[Lazy]}
        lazy={{ checkInView: true, loadPrevNext: true, loadPrevNextAmount: 3 }}
        centeredSlides
        onSwiper={setSwiper}
        simulateTouch
        slidesPerView={props.slidesPerView || 'auto'}
        spaceBetween={10}
        maxBackfaceHiddenSlides={5}
        // looping disabled when zooming into an image
        // the looped images aren't hidden when zooming and can appear on screen when enlarged
        loop={props.images.length > 1 && (!zoomImage || zoomImage.zoomPercentage <= 0)}
        style={{ height: '100%', maxHeight: props.maxHeight }}
        // prevent swiping to navigate on sitemap images on touch devices and when floor plans are zoomed in
        allowTouchMove={!(props.sitemap && touchDevice) && !(props.floorPlans && zoomImage.position.z !== 1)}
        // Update key for allowTouchMove to update, w/o key, the value of allowTouchMove does not effect ability to move
        key={`${props.sitemap}${touchDevice}${props.floorPlans}${zoomImage.position.z}`}
      >
        {props.children}

        {/* allow nav icons on sitemap images on touch devices */}
        {(props.sitemap || !touchDevice) && props.images.length > 1 && (
          <>
            <ArrowButton
              aria-label="Previous image"
              side="left"
              onClick={decrementIndex}
              data-testid="slider-left"
              sitemapTouch={touchDevice && props.sitemap}
            >
              <FontAwesomeIcon icon={faAngleLeft} color={theme.white} style={{ fontSize: 60 }} />
            </ArrowButton>

            <ArrowButton
              aria-label="Next image"
              side="right"
              onClick={incrementIndex}
              data-testid="slider-right"
              sitemapTouch={touchDevice && props.sitemap}
            >
              <FontAwesomeIcon icon={faAngleRight} color={theme.white} style={{ fontSize: 60 }} />
            </ArrowButton>
          </>
        )}

        {props.images.map((image, index) => {
          const imgAspectRatio = image.width / image.height;
          const isPanoramic = imgAspectRatio > MAX_RATIO;
          const className: string = props.fullscreen ? 'full-screen-img' : 'standard-img';

          return (
            <SwiperSlide key={image.key} style={{ aspectRatio: `${image.width} / ${image.height}` }}>
              <ImageBox
                onClick={() => handleImageClick(image)}
                data-testid={`slide-${index + 1}`}
                floorPlans={activeIndex === index && props.floorPlans}
              >
                {props.sitemap ? (
                  <Magnifier
                    id={`property-slider-image-${index}`}
                    src={image.image}
                    alt={`${props.alt} ${index + 1} of ${props.images?.length}`}
                    width={image.width}
                    height={image.height}
                    className={className}
                  />
                ) : props.floorPlans ? (
                  <PanZoomImage
                    id={`property-slider-image-${index}`}
                    src={image.image}
                    alt={`${props.alt} ${index + 1} of ${props.images?.length}`}
                    style={{
                      opacity: zoomImage && zoomImage.zoomPercentage > 0 && activeIndex !== index ? '0' : '1',
                    }}
                    position={activeIndex === index ? zoomImage.position : DEFAULT_ZOOM_POSITION}
                    setPosition={zoomImage.setPosition}
                    touchDistanceRef={zoomImage.touchDistanceRef}
                    positionRef={zoomImage.positionRef}
                    width={image.width}
                    height={image.height}
                    className={className}
                  />
                ) : (
                  <img
                    id={`property-slider-image-${index}`}
                    src={image.image}
                    alt={`${props.alt} ${index + 1} of ${props.images?.length}`}
                    loading={initialIndexes.current.includes(index) ? 'eager' : 'lazy'}
                    width={image.width}
                    height={image.height}
                    className={className}
                  />
                )}

                {props.renderImageOverlay && props.renderImageOverlay()}

                {isPanoramic && !props.fullscreen && (
                  <PanoramicIconBox>
                    <img
                      src={panoramicIcon.src}
                      width={panoramicIcon.width}
                      height={panoramicIcon.height}
                      alt="panoramic icon"
                    />
                  </PanoramicIconBox>
                )}

                {(image.type === 'webview' || image.type === 'video') && (
                  <ImageOverlay>
                    <TourIcon image={image} />

                    <Text>{image.text}</Text>
                  </ImageOverlay>
                )}
              </ImageBox>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </Container>
  );
};

export default PropertySlider;
